<p-toast></p-toast>
<p-confirmDialog [style]="{ width: '450px' }"></p-confirmDialog>

<div class="expense-page p-fluid">
    <ng-container *ngIf="canViewExpenses; else noPermission">
        <p-progressBar mode="indeterminate" class="page-progress" *ngIf="isLoading"></p-progressBar>


        <app-page-metric-strip [metrics]="getMetrics()"></app-page-metric-strip>

        <app-page-search-panel
            title="Search Expenses"
            [activeFilters]="activeSearchCount"
            [collapsed]="true"
            (clear)="clearSearch()"
            (search)="applySearch()">
            <div class="search-grid">
                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-search"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                            <input id="expenseGlobalSearch" pInputText [(ngModel)]="searchText" (keydown.enter)="applySearch()">
                            <label for="expenseGlobalSearch">Global Search</label>
                        </p-iftaLabel>
                    </p-inputGroup>
                </div>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-file"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                            <input id="expenseDescriptionSearch" pInputText [(ngModel)]="descriptionFilter">
                            <label for="expenseDescriptionSearch">Description</label>
                        </p-iftaLabel>
                    </p-inputGroup>
                </div>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                            <input id="expenseEmployeeSearch" pInputText [(ngModel)]="employeeFilter">
                            <label for="expenseEmployeeSearch">Submitted by</label>
                        </p-iftaLabel>
                    </p-inputGroup>
                </div>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                            <input id="expenseVendorSearch" pInputText [(ngModel)]="vendorFilter">
                            <label for="expenseVendorSearch">CRM Client / Party</label>
                        </p-iftaLabel>
                    </p-inputGroup>
                </div>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-flag"></i></p-inputGroupAddon>
                        <p-iftaLabel>
                            <p-select
                                inputId="expenseStatusSearch"
                                [options]="statusOptions"
                                optionLabel="label"
                                optionValue="value"
                                [(ngModel)]="statusFilter"
                                [showClear]="true"
                                styleClass="w-full">
                            </p-select>
                            <label for="expenseStatusSearch">Status</label>
                        </p-iftaLabel>
                    </p-inputGroup>
                </div>
            </div>
        </app-page-search-panel>

        <app-page-data-panel
            title="Expense Directory"
            [subtitle]="lastUpdatedText"
            [resultText]="expenses.length + ' visible of ' + totalRecords + ' records'">
            <div actions>
                <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" [outlined]="true" (click)="loadExpenses()"></p-button>
                <p-button label="Approval Queue" icon="pi pi-sitemap" severity="secondary" [outlined]="true" routerLink="/expense/approvals"></p-button>
                <p-button *ngIf="canCreateExpense" label="New Expense" icon="pi pi-plus" (click)="openNew()"></p-button>
            </div>
            <p-table
                [value]="expenses"
                [first]="lastLazyEvent.first ?? 0"
                [rows]="lastLazyEvent.rows ?? 10"
                [paginator]="true"
                [loading]="isLoading"
                [rowHover]="true"
                [totalRecords]="totalRecords"
                dataKey="expenseId"
                styleClass="p-datatable-sm p-datatable-gridlines expense-data-table"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} expenses"
                [showCurrentPageReport]="true"
                [tableStyle]="{ 'min-width': '70rem' }"
                (onPage)="loadExpensesLazy($event)">
                <ng-template pTemplate="header">
                    <tr>
                        <th style="width: 10rem">Expense</th>
                        <th>Description</th>
                        <th>Client / Submitted By</th>
                        <th>Financials</th>
                        <th>Approvals / Docs</th>
                        <th class="text-center" style="width: 9rem">Actions</th>
                    </tr>
                </ng-template>

                <ng-template pTemplate="body" let-expense>
                    <tr>
                        <td>
                            <div class="stack-cell expense-identity">
                                <span class="code-pill">{{ expense.expenseNumber || ('EXP-' + expense.expenseId) }}</span>
                                <p-tag [value]="expense.status || 'Draft'" [severity]="getStatusSeverity(expense.status)"></p-tag>
                            </div>
                        </td>
                        <td>
                            <div class="stack-cell">
                                <a class="link-button cell-title" [routerLink]="['/expense/view', expense.expenseId]">
                                    {{ expense.description || 'Expense without description' }}
                                </a>
                                <div class="cell-meta">
                                    <span><i class="pi pi-calendar"></i>{{ expense.submissionDate | date:'mediumDate' }}</span>
                                    <span><i class="pi pi-file-edit"></i>{{ expense.items?.length || 0 }} items</span>
                                    <span><i class="pi pi-comment"></i>{{ expense.notes ? 'Notes added' : 'No notes' }}</span>
                                </div>
                            </div>
                        </td>
                        <td>
                            <div class="stack-cell">
                                <span class="cell-title">{{ getClientDisplayName(expense) }}</span>
                                <div class="cell-meta vertical">
                                    <span><i class="pi pi-user"></i>Contact: {{ getContactDisplayName(expense) }}</span>
                                    <span><i class="pi pi-id-card"></i>Submitted by: {{ expense.submittedBy?.name || 'Unknown submitter' }}</span>
                                    <span *ngIf="expense.partyEntityType"><i class="pi pi-users"></i>Party type: {{ expense.partyEntityType }}</span>
                                    <span *ngIf="expense.partyContactDisplayName"><i class="pi pi-user"></i>Party contact: {{ expense.partyContactDisplayName }}</span>
                                    <span><i class="pi pi-wallet"></i>Paid by: {{ expense.owningCompany?.name || '-' }}</span>
                                </div>
                            </div>
                        </td>
                        <td>
                            <div class="stack-cell">
                                <span class="amount-pill">{{ expense.totalAmount | currency:(expense.currencyCode || 'SAR'):'symbol':'1.2-2' }}</span>
                                <div class="cell-meta vertical">
                                    <span><i class="pi pi-money-bill"></i>{{ expense.currencyCode || 'SAR' }}</span>
                                    <span><i class="pi pi-percentage"></i>{{ hasTaxes(expense) ? (getTotalTaxAmount(expense) | currency:(expense.currencyCode || 'SAR'):'symbol':'1.2-2') : 'No taxes' }}</span>
                                </div>
                            </div>
                        </td>
                        <td>
                            <div class="stack-cell">
                                <span class="cell-title">{{ expense.approvals?.length || 0 }} approval steps</span>
                                <div class="cell-meta vertical">
                                    <span><i class="pi pi-paperclip"></i>{{ expense.attachments?.length || 0 }} attachments</span>
                                    <span><i class="pi pi-clock"></i>{{ expense.updatedAt ? (expense.updatedAt | date:'medium') : 'Not updated yet' }}</span>
                                </div>
                            </div>
                        </td>
                        <td class="text-center">
                            <div class="row-actions">
                                <button pButton pRipple icon="pi pi-eye" class="p-button-rounded p-button-text p-button-info" [routerLink]="['/expense/view', expense.expenseId]" pTooltip="View expense"></button>
                                <button *ngIf="canEditExpense" pButton pRipple icon="pi pi-pencil" class="p-button-rounded p-button-text p-button-secondary" (click)="editExpense(expense)" pTooltip="Edit expense"></button>
                                <button *ngIf="canDeleteExpense" pButton pRipple icon="pi pi-trash" class="p-button-rounded p-button-text p-button-danger" (click)="deleteExpense(expense)" pTooltip="Delete expense"></button>
                            </div>
                        </td>
                    </tr>
                </ng-template>

                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="6">
                            <div class="empty-state">
                                <i class="pi pi-wallet"></i>
                                <strong>No expenses found</strong>
                                <span>Try broadening the search or capture a new expense record.</span>
                            </div>
                        </td>
                    </tr>
                </ng-template>
            </p-table>
        </app-page-data-panel>
    </ng-container>
</div>

<ng-template #noPermission>
    <div class="empty-state permission-state">
        <i class="pi pi-lock"></i>
        <strong>Expense access is not available</strong>
        <span>You do not have permission to view expense records.</span>
    </div>
</ng-template>
